import { sheetGroups, stepLabel, whyLocked } from '../signoff-policy';
import type { Signoff, StoredSignoffConfig, StoredSignoffPolicy } from '../types';
import SignatureRows from './SignatureRows';

/**
 * ── A RAISED SHEET, GROUPED THE WAY IT WAS RAISED — configurable sign-offs R1 ─
 *
 * The grouping, headings and locked-until-earlier-answers render used to live
 * inside SprintSignoff and only there, so a STAGE sheet — one raised from a
 * board's own named stages — showed its stage names on a finished sprint but as
 * a flat, headingless list on a release or a document. This is that render
 * lifted out, so all three sheets group and name their slices the same way.
 *
 *   STAGE SHEET — each board-defined stage is a labelled group, in the frozen
 *     order (`stageSeq`), named live from the policy (`stepLabel`). A later stage
 *     that may not be answered yet is drawn with no answer buttons and the line
 *     that says WHY (`whyLocked`), naming the stage that has to answer first.
 *   LEGACY CHAIN — with `groupLegacyChain` on (the sprint sheet only, #346), a
 *     rank chain groups by its fixed steps exactly as it did before.
 *   PLAIN / FLAT — a single unnamed approval, and (with `groupLegacyChain` off)
 *     every legacy release and document sheet, is drawn flat with no heading —
 *     byte-for-byte what those sheets did before this component existed.
 *
 * ⚠️ NOT THE SUMMARY LINE. Each screen keeps its own sentence about what the
 * signatures mean and how far along they are; this renders only the rows and
 * their per-stage headings, the one thing all three share.
 */
export default function SignoffSheet({
  rows,
  policy,
  mayActFor,
  maySendLink = false,
  locked = false,
  groupLegacyChain = false,
  verb = 'approve',
}: {
  rows: Signoff[];
  /** The board's stored sign-off policy — read live to name each stage. */
  policy: StoredSignoffPolicy | StoredSignoffConfig | undefined | null;
  mayActFor: boolean;
  maySendLink?: boolean;
  /** The whole sheet is closed to new answers — a withdrawn release, a
   *  superseded document. Rows still show; the per-stage lock is OR-ed onto it. */
  locked?: boolean;
  /** Group a legacy rank chain by its steps (the sprint sheet). Off elsewhere, so
   *  a legacy release or document keeps drawing flat exactly as it did. */
  groupLegacyChain?: boolean;
  /** The word for what a signature means on this subject. A test script is
   *  "signed off" (matching its external signing page); everything else
   *  "approves" (releases, change requests, charters, invoices). */
  verb?: 'approve' | 'signoff';
}) {
  const groups = sheetGroups(rows, groupLegacyChain);

  /* NO GROUPS IS DRAWN EXACTLY AS IT WAS — heading and all left to the caller.
     A plain approval gaining a heading is noise, and a legacy release or document
     that has always been flat must stay flat. */
  if (groups.length === 0)
    return (
      <SignatureRows
        rows={rows}
        mayActFor={mayActFor}
        maySendLink={maySendLink}
        locked={locked}
        verb={verb}
      />
    );

  return (
    <>
      {groups.map((step) => {
        /* THE ORDER IS A RULE, NOT A PICTURE — #346. A slice nobody may answer
           yet is drawn with no answer buttons and a line saying WHY, so a reader
           does not take the missing buttons for a fault and go round the sheet. */
        const why = whyLocked(step, rows, policy ?? undefined);
        return (
          <div className={`so-step ${why ? 'is-locked' : ''}`} key={step}>
            <div className="grp-heading">{stepLabel(step, policy)}</div>
            {why && (
              <p className="hint so-waiting-on" role="status">
                {why}
              </p>
            )}
            <SignatureRows
              rows={rows.filter((r) => (r.step ?? '') === step)}
              mayActFor={mayActFor}
              maySendLink={maySendLink}
              locked={locked || why !== ''}
              verb={verb}
            />
          </div>
        );
      })}
    </>
  );
}
